import {
  CANVAS_PAGE_WIDTH,
  parseCanvasPackage,
  type CanvasHostTheme,
} from '@littlebird/canvas';

import { electronApis } from '@/features/electron-api';

import { buildCanvasDocument } from './canvas-document';

import type {
  CanvasExportFormat,
  CanvasRenderTarget,
} from '../../../electron/shared';

export const CANVAS_RENDER_WIDTH = CANVAS_PAGE_WIDTH;

const MIME_TYPES: Record<CanvasExportFormat, string> = {
  png: 'image/png',
  pdf: 'application/pdf',
};

const toFilename = (
  title: string,
  format: CanvasExportFormat,
  suffix?: string
): string => {
  const cleaned = title.replace(/[\\/:*?"<>|]/g, '').trim();
  return `${cleaned || 'canvas'}${suffix ? `-${suffix}` : ''}.${format}`;
};

const renderCanvasBytes = async (
  canvasText: string,
  hostTheme: CanvasHostTheme,
  target: CanvasRenderTarget,
  layoutWidth: number
) => {
  const parseResult = parseCanvasPackage(canvasText);
  if (!parseResult.success) {
    return { ok: false as const, error: parseResult.error };
  }
  const result = await electronApis.canvasExport.render(
    buildCanvasDocument(parseResult.canvasPackage, hostTheme, {
      renderContext: target.kind === 'download' ? 'download' : 'embedded',
    }),
    layoutWidth,
    target
  );
  return result.ok
    ? {
        ok: true as const,
        bytes: result.bytes,
        title: parseResult.canvasPackage.title,
      }
    : { ok: false as const, error: result.error };
};

export type CanvasExportOutcome =
  | { ok: true; blob: Blob; filename: string }
  | { ok: false; error: string };

export const exportCanvas = async ({
  canvasText,
  hostTheme,
  format,
  filenameSuffix,
}: {
  canvasText: string;
  hostTheme: CanvasHostTheme;
  format: CanvasExportFormat;
  filenameSuffix?: string;
}): Promise<CanvasExportOutcome> => {
  const result = await renderCanvasBytes(
    canvasText,
    hostTheme,
    { kind: 'download', format },
    CANVAS_RENDER_WIDTH
  );
  if (!result.ok) return result;
  return {
    ok: true,
    blob: new Blob([new Uint8Array(result.bytes)], {
      type: MIME_TYPES[format],
    }),
    filename: toFilename(result.title, format, filenameSuffix),
  };
};

export const renderCanvasThumbnail = ({
  canvasText,
  hostTheme,
  maxHeight,
  layoutWidth,
}: {
  canvasText: string;
  hostTheme: CanvasHostTheme;
  maxHeight: number;
  layoutWidth: number;
}) =>
  renderCanvasBytes(
    canvasText,
    hostTheme,
    { kind: 'thumbnail', maxHeight },
    layoutWidth
  );

export type CanvasPreviewOutcome =
  | { ok: true; image: string }
  | { ok: false; error: string };

export const renderCanvasPreview = async ({
  canvasText,
  hostTheme,
  maxHeight,
  layoutWidth,
}: {
  canvasText: string;
  hostTheme: CanvasHostTheme;
  maxHeight: number;
  layoutWidth: number;
}): Promise<CanvasPreviewOutcome> => {
  const result = await renderCanvasBytes(
    canvasText,
    hostTheme,
    { kind: 'preview', maxHeight },
    layoutWidth
  );
  if (!result.ok) return result;
  const base64 = btoa(
    Array.from(new Uint8Array(result.bytes), (byte) =>
      String.fromCharCode(byte)
    ).join('')
  );
  return { ok: true, image: `data:image/jpeg;base64,${base64}` };
};
